/**
 * NativeCamera — drop-in replacement for the WASM web-gphoto2 `Camera` class,
 * backed by the gphoto2-daemon sidecar in the main process (flag `native_gphoto2`).
 *
 * Everything above the camera seam (uniCamUtils, Preview, health monitor, retry
 * classification) must not be able to tell the backends apart:
 * - same seven methods, same return types (Blob / File / config-tree object)
 * - errors carry the daemon's libgphoto2 message strings, which are the SAME
 *   strings the WASM build emits ("Perhaps no focus?", "PTP Device Busy", …), so
 *   `classifyCaptureError` keeps working unchanged
 * - ops serialize per instance; the sidecar additionally serializes per slot
 *
 * Differences, deliberate:
 * - `connect()` needs no WebUSB grant/gesture — the daemon owns the USB device
 *   through libusb/WinUSB directly. Callers skip the requestDevice() dance.
 * - `disconnect()` also stops the slot's daemon, releasing the USB claim.
 */

type SidecarSlot = 'primary' | 'secondary'

interface NativeCameraApi {
  isAvailable: () => Promise<boolean>
  op: (
    slot: SidecarSlot,
    op: string,
    args?: Record<string, unknown>
  ) => Promise<{ data: unknown; binary: Uint8Array | null }>
  stop: (slot: SidecarSlot) => Promise<void>
}

function getApi(): NativeCameraApi {
  const api = (window as { api?: { nativeCamera?: NativeCameraApi } }).api?.nativeCamera
  if (!api) throw new Error('native camera bridge unavailable (older packaged main)')
  return api
}

/** Feature-detect: flag callers must ALSO check this before constructing. */
export async function isNativeCameraAvailable(): Promise<boolean> {
  try {
    const api = (window as { api?: { nativeCamera?: NativeCameraApi } }).api?.nativeCamera
    if (!api) return false
    return await api.isAvailable()
  } catch {
    return false
  }
}

export class NativeCamera {
  #slot: SidecarSlot
  #queue: Promise<unknown> = Promise.resolve()
  #model: string | null = null

  /** libgphoto2's model string from the connect reply (e.g. "Canon EOS R100"), once connected. */
  get model(): string | null {
    return this.#model
  }

  constructor(options: { moduleSlot?: string } = {}) {
    this.#slot = options.moduleSlot === 'secondary' ? 'secondary' : 'primary'
  }

  /** Mirror web-gphoto2's per-instance op serialization. */
  #schedule<T>(fn: () => Promise<T>): Promise<T> {
    const run = this.#queue.then(fn, fn)
    this.#queue = run.catch(() => undefined)
    return run
  }

  async connect(): Promise<void> {
    await this.#schedule(async () => {
      const { data } = await getApi().op(this.#slot, 'connect', {})
      const model = (data as { model?: string } | null)?.model
      this.#model = model ?? null
      console.log(`[NATIVE-CAMERA:${this.#slot}] connected${model ? `: ${model}` : ''}`)
    })
  }

  /**
   * The daemon's protocol number (1 for builds that predate the field). The renderer
   * needs >= 2 — capture_no_af and set_single_config — and stays on WASM otherwise.
   */
  async protocolVersion(): Promise<number> {
    return this.#schedule(async () => {
      const { data } = await getApi().op(this.#slot, 'version', {})
      const p = (data as { protocol?: unknown } | null)?.protocol
      return typeof p === 'number' && Number.isFinite(p) ? p : 1
    })
  }

  async disconnect(): Promise<void> {
    await this.#schedule(async () => {
      try {
        await getApi().op(this.#slot, 'disconnect', {})
      } finally {
        await getApi().stop(this.#slot)
      }
    })
  }

  async getConfig(): Promise<unknown> {
    return this.#schedule(async () => (await getApi().op(this.#slot, 'get_config', {})).data)
  }

  async getSupportedOps(): Promise<Record<string, boolean>> {
    // The daemon backend supports the full standard surface; expressed statically
    // (the WASM build derives this from abilities, but every consumer only checks
    // these booleans).
    return {
      captureImage: true,
      captureVideo: false,
      captureAudio: false,
      capturePreview: true,
      config: true,
      triggerCapture: true
    }
  }

  async setConfigValue(
    name: string,
    value: string | number | boolean,
    options: { settle?: boolean } = {}
  ): Promise<void> {
    await this.#schedule(async () => {
      // One widget, not the whole tree: the daemon's `set_config` re-reads and
      // re-writes the entire config (0.5–2s on a Canon), which is what made every
      // autofocus drive or release cost a full tree round-trip. Older daemons
      // without the op answer "unknown op"; fall back to the tree write then.
      try {
        await getApi().op(this.#slot, 'set_single_config', { name, value })
      } catch (err) {
        if (!/unknown op/i.test(err instanceof Error ? err.message : String(err))) throw err
        await getApi().op(this.#slot, 'set_config', { name, value })
      }
    })
    // Parity with web-gphoto2: some configs report success before the camera has
    // actually applied them; the WASM class waits 800ms before letting the UI
    // re-read. Keep the same settle so operator-facing behavior matches — and skip it
    // on the same terms, for action widgets whose value is never read back.
    if (options.settle !== false) {
      await new Promise((resolve) => setTimeout(resolve, 800))
    }
  }

  async capturePreviewAsBlob(): Promise<Blob> {
    return this.#schedule(async () => {
      const { binary } = await getApi().op(this.#slot, 'capture_preview', {})
      if (!binary) throw new Error('preview returned no data')
      return new Blob([binary as BlobPart], { type: 'image/jpeg' })
    })
  }

  /**
   * Shutter press with NO autofocus (daemon op `capture_no_af`): the lens stays where
   * a countdown prefocus drive left it, and the camera fires immediately instead of
   * half-pressing with AF and waiting for a focus result first. Throws when the
   * daemon or the body cannot do it; callers fall back to captureImageAsFile().
   */
  async captureImageNoAf(timeoutMs = 10000): Promise<File> {
    return this.#schedule(async () => {
      const { data, binary } = await getApi().op(this.#slot, 'capture_no_af', { timeoutMs })
      if (!binary) throw new Error('capture_no_af returned no data')
      const name = (data as { name?: string } | null)?.name || 'capture.jpg'
      return new File([binary as BlobPart], name, { type: 'image/jpeg' })
    })
  }

  async captureImageAsFile(): Promise<File> {
    return this.#schedule(async () => {
      const { data, binary } = await getApi().op(this.#slot, 'capture_image', {})
      if (!binary) throw new Error('capture returned no data')
      const name = (data as { name?: string } | null)?.name || 'capture.jpg'
      return new File([binary as BlobPart], name, { type: 'image/jpeg' })
    })
  }

  async consumeEvents(): Promise<boolean> {
    return this.#schedule(async () => {
      const { data } = await getApi().op(this.#slot, 'drain_events', {})
      return ((data as { drained?: number } | null)?.drained ?? 0) > 0
    })
  }
}
